/** tm/components/MatchFillPanel.tsx — 매칭정보 일괄 채우기
 *  CompanyListPage 전용 진입점(GovPage는 수정 금지 대상이라 여기서만 연다). 매칭 정보 9개 필수 필드 중
 *  하나라도 비어있는 업체만 표로 모아, 셀 편집마다 디바운스 저장(PUT /companies/:id) — CompanyDetailPanel과 동일 패턴.
 *  업종·관심분류는 원래 다중 선택이지만 표 칸에서는 1개만 빠르게 고르는 것으로 간소화했다(세밀 편집은 업체 상세에서). */
import { GOV_TAXONOMY, canonCat } from './GovTaxonomy';
import DateInput from './DateInput';
import React, { useState, useRef, useMemo } from 'react';
import type { Company } from '../types';
import { C, T, R } from './ui';
import {
  type MatchFieldsValue, matchFieldsFromCompany, matchProgress, otherTagsOf, mergeTags,
  MATCH_REGIONS, MATCH_BIZ_TYPES, MATCH_SCALES,
} from './CompanyMatchFields';

interface Props {
  companies: Company[];
  onClose: () => void;
  onUpdate: (id: string, patch: Partial<Company>) => Promise<void>;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

const sel: React.CSSProperties = { width: '100%', minHeight: 36, padding: '6px 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: 12.5, outline: 'none', boxSizing: 'border-box' as const, background: '#fff' };
const th: React.CSSProperties = { padding: '8px', fontSize: 11.5, fontWeight: 700, color: C.sub, textAlign: 'left' as const, borderBottom: `2px solid ${C.border}`, background: C.bgAlt, whiteSpace: 'nowrap' as const, position: 'sticky' as const, top: 0, zIndex: 1 };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle' as const };
const COLS = ['지역','업종','기업규모','창업기업 여부','대표 생년월일','상시근로자수','연매출(억원)','설립일','관심 분류'];

export default function MatchFillPanel({ companies, onClose, onUpdate, onToast }: Props) {
  const [overrides, setOverrides] = useState<Record<string, Partial<MatchFieldsValue>>>({});
  const pendingRef = useRef<Record<string, Partial<MatchFieldsValue>>>({});
  const timersRef = useRef<Record<string, number>>({});

  // 서버 데이터 기준으로 한 번만 정렬/필터 — 입력 중 행이 튀지 않도록 overrides는 여기서 안 쓴다
  const rows = useMemo(() => companies
    .map(c => ({ c, p: matchProgress(matchFieldsFromCompany(c)) }))
    .filter(r => r.p.filled < r.p.total)
    .sort((a, b) => a.p.filled - b.p.filled || a.c.name.localeCompare(b.c.name)),
    [companies]);

  function valueOf(c: Company): MatchFieldsValue {
    return { ...matchFieldsFromCompany(c), ...(overrides[c.id] || {}) };
  }

  async function flush(id: string) {
    const patch = pendingRef.current[id];
    if (!patch || !Object.keys(patch).length) return;
    const company = companies.find(x => x.id === id);
    if (!company) return;
    const next = { ...matchFieldsFromCompany(company), ...patch };
    const apiPatch: Record<string, unknown> = {};
    if ('region' in patch) apiPatch.region = next.region;
    if ('biz_types' in patch) apiPatch.biz_types = next.biz_types;
    if ('company_scale' in patch) apiPatch.company_scale = next.company_scale;
    if ('is_startup' in patch) apiPatch.is_startup = next.is_startup;
    if ('ceo_birth' in patch) apiPatch.ceo_birth = next.ceo_birth || null;
    if ('employees' in patch) apiPatch.employees = next.employees.trim() !== '' ? Number(next.employees) : null;
    if ('sales_eok' in patch) apiPatch.sales_eok = next.sales_eok.trim() !== '' ? Number(next.sales_eok) : null;
    if ('established_date' in patch) apiPatch.established_date = next.established_date || null;
    if ('tags' in patch || 'arts' in patch) apiPatch.tags = mergeTags(otherTagsOf(company.tags), next);   // 전용 분야·기업 유형·전용 대상 칩도 (예전엔 관심 분류만 저장)
    if ('sectors_off' in patch) apiPatch.sectors_off = next.sectors_off;
    delete pendingRef.current[id];
    setOverrides(prev => { const cp = { ...prev }; delete cp[id]; return cp; });
    try { await onUpdate(id, apiPatch as Partial<Company>); } catch { onToast('저장 실패', 'error'); }
  }

  function edit(id: string, patch: Partial<MatchFieldsValue>) {
    const merged = { ...(pendingRef.current[id] || {}), ...patch };
    pendingRef.current[id] = merged;
    setOverrides(prev => ({ ...prev, [id]: merged }));
    if (timersRef.current[id]) window.clearTimeout(timersRef.current[id]);
    timersRef.current[id] = window.setTimeout(() => flush(id), 500);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{ position: 'relative', width: 'min(96vw, 1360px)', maxHeight: '90vh', background: C.white, borderRadius: R.lg, display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: T.lg, fontWeight: 700, color: '#fff' }}>📝 매칭정보 채우기</div>
            <div style={{ fontSize: T.xs, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>매칭 정보가 부족한 업체 {rows.length}곳 · 칸을 채우면 자동 저장됩니다</div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
          {rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>🎉 모든 업체의 매칭 정보가 채워져 있습니다</div>
          ) : (
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1180 }}>
              <thead>
                <tr>
                  <th style={{ ...th, left: 0, zIndex: 2, minWidth: 150 }}>업체명</th>
                  <th style={{ ...th, minWidth: 56 }}>진행</th>
                  {COLS.map(c => <th key={c} style={{ ...th, minWidth: 110 }}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ c }) => {
                  const v = valueOf(c);
                  const p = matchProgress(v);
                  const done = p.filled === p.total;
                  return (
                    <tr key={c.id}>
                      <td style={{ ...td, position: 'sticky' as const, left: 0, background: C.white, fontWeight: 700, fontSize: 12.5, zIndex: 1 }}>{c.name}</td>
                      <td style={td}><span style={{ fontSize: 11, fontWeight: 700, color: done ? C.green : '#D97706' }}>{p.filled}/{p.total}</span></td>
                      <td style={td}>
                        <select style={sel} value={v.region} onChange={e => edit(c.id, { region: e.target.value })}>
                          <option value="">-</option>{MATCH_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <select style={sel} value={v.biz_types[0] || ''} onChange={e => edit(c.id, { biz_types: e.target.value ? [e.target.value] : [] })}>
                          <option value="">-</option>{MATCH_BIZ_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <select style={sel} value={v.company_scale} onChange={e => edit(c.id, { company_scale: e.target.value })}>
                          <option value="">-</option>{MATCH_SCALES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <select style={sel} value={v.is_startup === null ? '' : (v.is_startup ? 'y' : 'n')}
                          onChange={e => edit(c.id, { is_startup: e.target.value === '' ? null : e.target.value === 'y' })}>
                          <option value="">-</option><option value="y">예</option><option value="n">아니오</option>
                        </select>
                      </td>
                      <td style={td}><DateInput style={sel} value={v.ceo_birth} onChange={x => edit(c.id, { ceo_birth: x })} aria-label="대표 생년월일" /></td>
                      <td style={td}><input type="number" min={0} style={sel} placeholder="명" value={v.employees} onChange={e => edit(c.id, { employees: e.target.value })} /></td>
                      <td style={td}><input type="number" min={0} step="0.1" style={sel} placeholder="억원" value={v.sales_eok} onChange={e => edit(c.id, { sales_eok: e.target.value })} /></td>
                      <td style={td}><DateInput style={sel} value={v.established_date} onChange={x => edit(c.id, { established_date: x })} aria-label="설립일" /></td>
                      <td style={td}>
                        <select style={sel} value={v.tags[0] ? canonCat(v.tags[0]) : ''} onChange={e => edit(c.id, { tags: e.target.value ? [e.target.value] : [] })}>
                          <option value="">-</option>
                          {GOV_TAXONOMY.map(g => <optgroup key={g.k} label={g.name}>{!g.noGroupLabel && <option value={g.name}>{g.name}{g.loan ? '' : ' 전체'}</option>}{g.items.map(it => <option key={it} value={it}>{it}</option>)}</optgroup>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: '10px 20px', borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted, flexShrink: 0 }}>
          업종·관심분류는 원래 여러 개를 고를 수 있어요 — 여기서는 1개만 빠르게 채우고, 여러 개를 고르려면 업체 상세에서 편집하세요.
        </div>
      </div>
    </div>
  );
}
